import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Search as SearchIcon,
  PersonRemove as RemoveIcon,
  SwapHoriz as MoveIcon
} from '@mui/icons-material';
import api from '../../../services/api';

function CurrentMembersTab({ serviceGroup, token, members, onRefresh }) {
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTherapists, setSelectedTherapists] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [selectedTrainingAdmins, setSelectedTrainingAdmins] = useState([]);
  const [selectedLecturers, setSelectedLecturers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search filters
  const [clientSearch, setClientSearch] = useState('');
  const [therapistSearch, setTherapistSearch] = useState('');
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [trainingAdminSearch, setTrainingAdminSearch] = useState('');
  const [lecturerSearch, setLecturerSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, role: '', count: 0 });

  // Move dialog state
  const [moveDialog, setMoveDialog] = useState({ open: false, role: '', selectedIds: [] });
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedDestinationGroup, setSelectedDestinationGroup] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  // Load available service groups for move operation
  useEffect(() => {
    loadAvailableGroups();
  }, []);

  const loadAvailableGroups = async () => {
    try {
      const response = await api.get('/service-groups?includeArchived=false');
      // Filter out the current group
      const groups = (response.data.data || []).filter(g => g._id !== serviceGroup._id);
      setAvailableGroups(groups);
    } catch (err) {
      console.error('Error loading service groups:', err);
    }
  };

  // Group members by role
  const clients = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('client');
  });

  const therapists = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('therapist');
  });

  const supervisors = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('supervisor');
  });

  const trainingAdmins = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('training_admin');
  });

  const lecturers = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('lecturer');
  });

  const students = members.filter(m => {
    const membership = m.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.roles?.includes('student');
  });

  // Apply search filters
  const filteredClients = clients.filter(m =>
    !clientSearch || m.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredTherapists = therapists.filter(m =>
    !therapistSearch || m.name?.toLowerCase().includes(therapistSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(therapistSearch.toLowerCase())
  );

  const filteredSupervisors = supervisors.filter(m =>
    !supervisorSearch || m.name?.toLowerCase().includes(supervisorSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(supervisorSearch.toLowerCase())
  );

  const filteredTrainingAdmins = trainingAdmins.filter(m =>
    !trainingAdminSearch || m.name?.toLowerCase().includes(trainingAdminSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(trainingAdminSearch.toLowerCase())
  );

  const filteredLecturers = lecturers.filter(m =>
    !lecturerSearch || m.name?.toLowerCase().includes(lecturerSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(lecturerSearch.toLowerCase())
  );

  const filteredStudents = students.filter(m =>
    !studentSearch || m.name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const isTrainingOrg = serviceGroup.groupType === 'educational_institution' || serviceGroup.groupType === 'training_program' || serviceGroup.trainingConfig?.enabled;

  const getJoinedDate = (member) => {
    const membership = member.serviceGroups?.find(sg =>
      (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
    );
    return membership?.joinedDate ? new Date(membership.joinedDate).toLocaleDateString() : 'N/A';
  };

  const handleBulkRemove = async (role, selectedIds) => {
    if (selectedIds.length === 0) {
      setError(`Please select at least one ${role} to remove`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Remove each selected member
      for (const userId of selectedIds) {
        await api.delete(`/service-groups/${serviceGroup._id}/members/${userId}`);
      }

      setSuccess(`Successfully removed ${selectedIds.length} ${role}(s) from the group`);

      // Clear selections
      if (role === 'client') setSelectedClients([]);
      if (role === 'therapist') setSelectedTherapists([]);
      if (role === 'supervisor') setSelectedSupervisors([]);

      onRefresh();
    } catch (err) {
      console.error('Error removing members:', err);
      setError(err.response?.data?.msg || 'Failed to remove members');
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, role: '', count: 0 });
    }
  };

  const openConfirmDialog = (role, selectedIds) => {
    setConfirmDialog({ open: true, role, count: selectedIds.length });
  };

  const openMoveDialog = (role, selectedIds) => {
    setMoveDialog({ open: true, role, selectedIds });
    setSelectedDestinationGroup('');
  };

  const handleBulkMove = async () => {
    if (!selectedDestinationGroup) {
      setError('Please select a destination group');
      return;
    }

    try {
      setMoveLoading(true);
      setError('');
      setSuccess('');

      // Get the role for these users in the current group
      const usersToMove = members.filter(m => moveDialog.selectedIds.includes(m._id));

      // Move each selected member
      for (const user of usersToMove) {
        const currentMembership = user.serviceGroups?.find(sg =>
          (typeof sg.groupId === 'string' ? sg.groupId : sg.groupId?.toString()) === serviceGroup._id
        );
        const rolesInGroup = currentMembership?.roles || ['client'];

        // Remove from current group
        await api.delete(`/service-groups/${serviceGroup._id}/members/${user._id}`);

        // Add to destination group with same roles
        await api.post(`/service-groups/${selectedDestinationGroup}/members`, {
          userId: user._id,
          role: rolesInGroup,
          notes: `Moved from "${serviceGroup.name}" on ${new Date().toLocaleDateString()}`
        });
      }

      setSuccess(`Successfully moved ${moveDialog.selectedIds.length} ${moveDialog.role} to the new group`);

      // Clear selections
      if (moveDialog.role === 'clients') setSelectedClients([]);
      if (moveDialog.role === 'therapists') setSelectedTherapists([]);
      if (moveDialog.role === 'supervisors') setSelectedSupervisors([]);

      setMoveDialog({ open: false, role: '', selectedIds: [] });
      onRefresh();
    } catch (err) {
      console.error('Error moving members:', err);
      setError(err.response?.data?.msg || 'Failed to move members');
    } finally {
      setMoveLoading(false);
    }
  };

  const renderMemberTable = (memberList, selectedIds, setSelectedIds, searchValue, setSearchValue, roleLabel) => {
    const handleSelectAll = (event) => {
      if (event.target.checked) {
        setSelectedIds(memberList.map(m => m._id));
      } else {
        setSelectedIds([]);
      }
    };

    const handleToggleMember = (memberId) => {
      if (selectedIds.includes(memberId)) {
        setSelectedIds(selectedIds.filter(id => id !== memberId));
      } else {
        setSelectedIds([...selectedIds, memberId]);
      }
    };

    return (
      <Box>
        {/* Search */}
        <TextField
          size="small"
          fullWidth
          label={`Search ${roleLabel}`}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < memberList.length}
                    checked={memberList.length > 0 && selectedIds.length === memberList.length}
                    onChange={handleSelectAll}
                    disabled={memberList.length === 0}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Joined Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No {roleLabel.toLowerCase()} in this group
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                memberList.map((member) => (
                  <TableRow
                    key={member._id}
                    hover
                    onClick={() => handleToggleMember(member._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(member._id)} />
                    </TableCell>
                    <TableCell>{member.name || 'N/A'}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{getJoinedDate(member)}</TableCell>
                    <TableCell>
                      <Chip
                        label={member.status || 'Unknown'}
                        size="small"
                        color={member.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action Buttons */}
        {memberList.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<MoveIcon />}
              onClick={() => openMoveDialog(roleLabel.toLowerCase(), selectedIds)}
              disabled={selectedIds.length === 0 || loading || availableGroups.length === 0}
            >
              Move {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}to Another Group
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RemoveIcon />}
              onClick={() => openConfirmDialog(roleLabel.toLowerCase(), selectedIds)}
              disabled={selectedIds.length === 0 || loading}
            >
              Remove {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}Selected
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Clients Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="h6">
            Clients ({clients.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderMemberTable(
            filteredClients,
            selectedClients,
            setSelectedClients,
            clientSearch,
            setClientSearch,
            'Clients'
          )}
        </AccordionDetails>
      </Accordion>

      {/* Therapists Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="h6">
            Therapists ({therapists.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderMemberTable(
            filteredTherapists,
            selectedTherapists,
            setSelectedTherapists,
            therapistSearch,
            setTherapistSearch,
            'Therapists'
          )}
        </AccordionDetails>
      </Accordion>

      {/* Clinical Supervisors Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="h6">
            Clinical Supervisors ({supervisors.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderMemberTable(
            filteredSupervisors,
            selectedSupervisors,
            setSelectedSupervisors,
            supervisorSearch,
            setSupervisorSearch,
            'Supervisors'
          )}
        </AccordionDetails>
      </Accordion>

      {/* Training Admins Section */}
      {isTrainingOrg && (
        <Accordion defaultExpanded={trainingAdmins.length > 0}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="h6">
              Training Admins ({trainingAdmins.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderMemberTable(
              filteredTrainingAdmins,
              selectedTrainingAdmins,
              setSelectedTrainingAdmins,
              trainingAdminSearch,
              setTrainingAdminSearch,
              'Training Admins'
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Lecturers Section */}
      {isTrainingOrg && (
        <Accordion defaultExpanded={lecturers.length > 0}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="h6">
              Lecturers ({lecturers.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderMemberTable(
              filteredLecturers,
              selectedLecturers,
              setSelectedLecturers,
              lecturerSearch,
              setLecturerSearch,
              'Lecturers'
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Students Section */}
      {isTrainingOrg && (
        <Accordion defaultExpanded={students.length > 0}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="h6">
              Students ({students.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderMemberTable(
              filteredStudents,
              selectedStudents,
              setSelectedStudents,
              studentSearch,
              setStudentSearch,
              'Students'
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, role: '', count: 0 })}
      >
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {confirmDialog.count} {confirmDialog.role} from "{serviceGroup.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, role: '', count: 0 })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.role === 'clients') handleBulkRemove('client', selectedClients);
              if (confirmDialog.role === 'therapists') handleBulkRemove('therapist', selectedTherapists);
              if (confirmDialog.role === 'supervisors') handleBulkRemove('supervisor', selectedSupervisors);
            }}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Dialog */}
      <Dialog
        open={moveDialog.open}
        onClose={() => setMoveDialog({ open: false, role: '', selectedIds: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move {moveDialog.selectedIds.length} {moveDialog.role} to Another Group</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the destination service group. Members will be moved from "{serviceGroup.name}" to the selected group
            with the same role they currently have.
          </DialogContentText>

          <FormControl fullWidth>
            <InputLabel>Destination Group</InputLabel>
            <Select
              value={selectedDestinationGroup}
              onChange={(e) => setSelectedDestinationGroup(e.target.value)}
              label="Destination Group"
            >
              {availableGroups.length === 0 ? (
                <MenuItem disabled>No other groups available</MenuItem>
              ) : (
                availableGroups.map((group) => (
                  <MenuItem key={group._id} value={group._id}>
                    {group.name} ({group.stats?.totalClients || 0} clients, {group.stats?.totalTherapists || 0} therapists)
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false, role: '', selectedIds: [] })}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkMove}
            color="primary"
            variant="contained"
            disabled={!selectedDestinationGroup || moveLoading}
            startIcon={moveLoading ? <CircularProgress size={16} /> : <MoveIcon />}
          >
            {moveLoading ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CurrentMembersTab;
